/**
 * External User Portal — simplified read-only view for external / counterparty users
 * Also accessible by internal users via /portal route for preview
 */
import React, { useState, useEffect } from 'react';
import { API } from '../App';

const STAGES = {
  initial:    { label: 'Initial',    color: 'var(--text3)' },
  kyc:        { label: 'KYC',        color: '#f59e0b' },
  loi:        { label: 'LOI',        color: '#3b82f6' },
  spa:        { label: 'SPA',        color: '#8b5cf6' },
  execution:  { label: 'Execution',  color: '#ec4899' },
  settlement: { label: 'Settlement', color: '#22c55e' },
  billing:    { label: 'Billing',    color: '#10b981' },
  completed:  { label: 'Completed',  color: '#059669' },
};

const DOC_STATUSES = {
  pending:  { label: 'Ausstehend', color: 'var(--text3)' },
  received: { label: 'Eingegangen',color: '#f59e0b' },
  approved: { label: 'Genehmigt', color: '#22c55e' },
  rejected: { label: 'Abgelehnt', color: '#ef4444' },
};

const LC_STATUSES = {
  draft:     { label: 'Entwurf',     color: 'var(--text3)' },
  issued:    { label: 'Ausgestellt', color: '#3b82f6' },
  advised:   { label: 'Avisiert',    color: '#8b5cf6' },
  confirmed: { label: 'Bestätigt',   color: '#22c55e' },
  utilized:  { label: 'Abgerufen',   color: '#f59e0b' },
  expired:   { label: 'Abgelaufen',  color: '#ef4444' },
};

function DocBadge({ status }) {
  const m = DOC_STATUSES[status] || DOC_STATUSES.pending;
  return (
    <span style={{ display: 'inline-flex', padding: '2px 7px', borderRadius: 4,
      fontSize: 10, fontWeight: 700, background: m.color + '22', color: m.color }}>
      {m.label}
    </span>
  );
}

function StageBadge({ stage }) {
  const m = STAGES[stage] || { label: stage, color: 'var(--text3)' };
  return (
    <span style={{ display: 'inline-flex', padding: '2px 8px', borderRadius: 4,
      fontSize: 11, fontWeight: 700, background: m.color + '22', color: m.color }}>
      {m.label}
    </span>
  );
}

export default function ExternalPortalPage() {
  const [deals, setDeals]   = useState([]);
  const [lcs, setLcs]       = useState([]);
  const [loading, setLoad]  = useState(true);
  const [selected, setSel]  = useState(null);
  const [detail, setDetail] = useState(null);
  const [tab, setTab]       = useState('deals');

  useEffect(() => {
    Promise.all([
      API.get('/portal/deals'),
      API.get('/portal/lc-sblc'),
    ]).then(([d, l]) => {
      setDeals(d.data);
      setLcs(l.data);
    }).finally(() => setLoad(false));
  }, []);

  const openDeal = async (deal) => {
    const r = await API.get(`/portal/deals/${deal.id}`);
    setDetail(r.data);
    setSel(deal);
  };

  if (detail && selected) {
    return (
      <div className="page-content">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => { setSel(null); setDetail(null); }}>
            ← Zurück
          </button>
          <h1 style={{ fontSize: 18, fontWeight: 700 }}>{detail.reference} — {detail.title}</h1>
          <StageBadge stage={detail.stage} />
        </div>

        <div className="grid-2" style={{ marginBottom: 16 }}>
          <div className="card">
            <div className="card-title">Cargo Details</div>
            {[
              ['Produkt', detail.product_type],
              ['Menge', detail.quantity],
              ['Incoterm', detail.incoterm],
              ['Port of Loading', detail.port_of_loading],
              ['Port of Discharge', detail.port_of_discharge],
              ['Laycan', detail.laycan_start && detail.laycan_end
                ? `${detail.laycan_start} – ${detail.laycan_end}` : detail.laycan_start],
              ['ETA', detail.eta],
              ['Kontraktdatum', detail.contract_date],
            ].filter(([, v]) => v).map(([k, v]) => (
              <div key={k} style={{ display: 'flex', gap: 8, padding: '6px 0',
                borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                <span style={{ color: 'var(--text3)', minWidth: 150 }}>{k}</span>
                <span style={{ color: 'var(--text1)' }}>{v}</span>
              </div>
            ))}
          </div>

          <div className="card">
            <div className="card-title">Dokumentenstatus</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[
                { label: 'Letter of Intent (LOI)', key: 'loi_status' },
                { label: 'Sales & Purchase Agreement (SPA)', key: 'spa_status' },
                { label: 'Letter of Credit (LC)', key: 'lc_status' },
                { label: 'Bill of Lading (BL)', key: 'bl_status' },
              ].map(({ label, key }) => (
                <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 13, color: 'var(--text2)' }}>{label}</span>
                  <DocBadge status={detail[key]} />
                </div>
              ))}
            </div>
          </div>
        </div>

        {(detail.buyer_name || detail.seller_name) && (
          <div className="card">
            <div className="card-title">Parteien</div>
            <div className="grid-2">
              {detail.buyer_name && (
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 2 }}>BUYER</div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{detail.buyer_name}</div>
                </div>
              )}
              {detail.seller_name && (
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 2 }}>SELLER</div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{detail.seller_name}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {detail.notes && (
          <div className="card">
            <div className="card-title">Notizen</div>
            <p style={{ fontSize: 13, color: 'var(--text2)', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
              {detail.notes}
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="page-header">
        <h1>🌐 External Portal</h1>
        <p>Lesezugriff auf aktive Deals und LC/SBLC-Übersicht</p>
      </div>

      {/* Read-only notice */}
      <div style={{ padding: '10px 14px', marginBottom: 20, borderRadius: 8,
        background: '#8b5cf615', border: '1px solid #8b5cf640', fontSize: 13, color: '#8b5cf6' }}>
        🔒 Nur-Lesen Modus — Keine Änderungen möglich
      </div>

      {/* Summary */}
      {!loading && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
          {[
            { label: 'Aktive Deals', value: deals.filter(d => !['completed'].includes(d.stage)).length, color: '#3b82f6' },
            { label: 'LC/SBLC',      value: lcs.length, color: '#22c55e' },
          ].map(c => (
            <div key={c.label} className="card" style={{ minWidth: 120, textAlign: 'center', padding: '14px 20px' }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: c.color }}>{c.value}</div>
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>{c.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border)', marginBottom: 20 }}>
        {[{ id: 'deals', label: '📁 Deals' }, { id: 'lc', label: '🏦 LC/SBLC' }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className="btn btn-ghost btn-sm"
            style={{ borderBottom: tab === t.id ? '2px solid var(--accent)' : '2px solid transparent',
              borderRadius: '6px 6px 0 0', color: tab === t.id ? 'var(--accent)' : 'var(--text2)' }}>
            {t.label}
          </button>
        ))}
      </div>

      {loading && <div className="loading">Lade…</div>}

      {/* Deals tab */}
      {!loading && tab === 'deals' && (
        <div>
          {deals.length === 0 && (
            <div style={{ textAlign: 'center', padding: 60, color: 'var(--text3)' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📁</div>
              <div>Keine aktiven Deals</div>
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {deals.map(d => (
              <div key={d.id} className="card" style={{ cursor: 'pointer' }} onClick={() => openDeal(d)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700, fontSize: 14 }}>{d.reference}</span>
                      <span style={{ fontSize: 14, color: 'var(--text2)' }}>{d.title}</span>
                      <StageBadge stage={d.stage} />
                    </div>
                    <div style={{ display: 'flex', gap: 16, fontSize: 12, color: 'var(--text3)', flexWrap: 'wrap' }}>
                      {d.product_type && <span>{d.product_type}</span>}
                      {d.quantity && <span>{d.quantity}</span>}
                      {d.incoterm && <span>{d.incoterm}</span>}
                      {d.port_of_loading && <span>↑ {d.port_of_loading}</span>}
                      {d.port_of_discharge && <span>↓ {d.port_of_discharge}</span>}
                    </div>
                  </div>
                  <span style={{ fontSize: 18, color: 'var(--text3)' }}>›</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* LC/SBLC tab */}
      {!loading && tab === 'lc' && (
        <div>
          {lcs.length === 0 && (
            <div style={{ textAlign: 'center', padding: 60, color: 'var(--text3)' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🏦</div>
              <div>Keine LC/SBLC-Einträge</div>
            </div>
          )}
          <table className="tbl">
            {lcs.length > 0 && (
              <thead><tr>
                <th>Typ / Referenz</th>
                <th>Applicant</th>
                <th>Beneficiary</th>
                <th>Betrag</th>
                <th>Ablauf</th>
                <th>Status</th>
              </tr></thead>
            )}
            <tbody>
              {lcs.map(r => {
                const stm = LC_STATUSES[r.status] || { label: r.status, color: 'var(--text3)' };
                return (
                  <tr key={r.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{r.lc_type}</div>
                      {r.reference_number && <div style={{ fontSize: 11, color: 'var(--text3)' }}>{r.reference_number}</div>}
                      {r.title && <div style={{ fontSize: 11, color: 'var(--text3)' }}>{r.title}</div>}
                    </td>
                    <td style={{ color: 'var(--text2)', fontSize: 13 }}>{r.applicant_name || '—'}</td>
                    <td style={{ color: 'var(--text2)', fontSize: 13 }}>{r.beneficiary_name || '—'}</td>
                    <td style={{ fontSize: 13 }}>{r.amount ? `${r.amount} ${r.currency}` : '—'}</td>
                    <td style={{ fontSize: 13, color: 'var(--text2)' }}>{r.expiry_date || '—'}</td>
                    <td>
                      <span style={{ display: 'inline-flex', padding: '2px 8px', borderRadius: 4,
                        fontSize: 11, fontWeight: 700, background: stm.color + '22', color: stm.color }}>
                        {stm.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
